/**
 * LanguageSwitcher — a compact flag+name dropdown for switching UI language.
 *
 * Renders as a `<button>` showing the active language flag and code.
 * On click, a small dropdown lists the other two options.
 * Clicking an option calls `useLanguageSwitcher().switchLanguage(code)`.
 */
import { useState, useRef, useEffect } from 'react'
import { Globe } from 'lucide-react'
import { useLanguageSwitcher } from '../hooks/useI18nNavigate'
import type { SupportedLanguage } from '../i18n'

// ── Language metadata ─────────────────────────────────────────────────────────

const LANGUAGES: { code: SupportedLanguage; label: string; flag: string }[] = [
  { code: 'en', label: 'English',  flag: '🇺🇸' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'es', label: 'Español',  flag: '🇪🇸' },
]

// ── Component ─────────────────────────────────────────────────────────────────

interface LanguageSwitcherProps {
  /** Extra Tailwind classes applied to the root wrapper */
  className?: string
  /** `compact` matches the landing-page header style (globe + code only). */
  variant?: 'default' | 'compact'
}

export function LanguageSwitcher({ className = '', variant = 'default' }: LanguageSwitcherProps) {
  const { currentLang, switchLanguage } = useLanguageSwitcher()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const active = LANGUAGES.find(l => l.code === currentLang) ?? LANGUAGES[0]

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(v => !v)}
        className={
          variant === 'compact'
            ? 'flex items-center gap-1.5 text-[#4a5565] hover:text-[#101828] transition-colors'
            : 'flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors px-2 py-1 rounded-lg hover:bg-gray-50'
        }
        aria-label="Switch language"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className={variant === 'compact' ? 'w-3.5 h-3.5 shrink-0' : 'w-3.5 h-3.5 shrink-0'} />
        {variant !== 'compact' && (
          <span className="text-base leading-none">{active.flag}</span>
        )}
        <span
          className={
            variant === 'compact'
              ? 'text-xs font-medium uppercase tracking-[0.3px]'
              : 'text-xs font-medium uppercase tracking-wide hidden sm:block'
          }
        >
          {active.code}
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          role="listbox"
          className="absolute end-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 shadow-lg overflow-hidden z-[100] min-w-[140px]"
        >
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              role="option"
              aria-selected={lang.code === currentLang}
              onClick={() => {
                setOpen(false)
                if (lang.code !== currentLang) {
                  switchLanguage(lang.code)
                }
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-start transition-colors hover:bg-gray-50 ${
                lang.code === currentLang
                  ? 'text-gray-900 font-semibold bg-gray-50'
                  : 'text-gray-600'
              }`}
            >
              <span className="text-base leading-none">{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
